import { useEffect, useState } from 'react';
import { useAppUser } from '../auth/AppUserContext';
import { getVisibleStores } from '../data/stores';
import type { Store } from '../data/types';
import { getUserDisplayName } from '../graph/directory';
import { createNameCache, dmNameFor, shortAddress, storeAccountLabel } from './people';

/**
 * How a person or account is SHOWN (DM exploration 2026-10-19: cards said
 * "by auditplus-test" / "assigned to auditplus-testmail" while the task page
 * said "00001 — Test Store" for the same account). One rule everywhere
 * (ui/people.ts): a store's shared account → "00001 — Store name"; anyone
 * else → their directory display name; a district DM the directory doesn't
 * know → the DM name on their stores' rows (round 6: "by
 * sim-auditplus-stores+dm2000" on audit cards); until (or unless) one of
 * those is known → the part of the address before the @. The full address stays in the
 * hover title.
 */

/** Directory display name, looked up once per address per page load; null when unknown. */
export const lookupDisplayName = createNameCache(getUserDisplayName);

/** The label for `upn` (see the file comment); starts as the short address. */
export function usePersonLabel(upn: string): string {
  const { user } = useAppUser();
  const [label, setLabel] = useState(() => shortAddress(upn));
  useEffect(() => {
    let live = true;
    setLabel(shortAddress(upn));
    if (!upn.trim()) return;
    void (async () => {
      // Labels only, so inactive (divested) stores' accounts still read as
      // their store. The viewer's stores ("View as" narrows them too);
      // cached and shared (graph/readCache) — one read however many names
      // a page shows.
      const stores = user ? await getVisibleStores(user, { includeInactive: true }).catch(() => [] as Store[]) : [];
      const store = storeAccountLabel(stores, upn);
      if (store) {
        if (live) setLabel(store);
        return;
      }
      const name = (await lookupDisplayName(upn)) ?? dmNameFor(stores, upn);
      if (live && name) setLabel(name);
    })();
    return () => {
      live = false;
    };
  }, [upn, user]);
  return label;
}

export function PersonName({ upn }: { upn: string }) {
  const label = usePersonLabel(upn);
  return <span title={upn.trim() || undefined}>{label}</span>;
}
